import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Accessibility, 
  ZoomIn, 
  ZoomOut, 
  Contrast, 
  MousePointer2,
  Link2,
  RotateCcw,
  FileText
} from "lucide-react";
import { Link } from "react-router-dom";

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  highlightLinks: boolean;
  bigCursor: boolean;
  disableAnimations: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  bigCursor: false,
  disableAnimations: false,
};

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const saved = localStorage.getItem("accessibilitySettings");
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem("accessibilitySettings", JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${s.fontSize}%`;
    
    // High contrast
    if (s.highContrast) {
      root.classList.add("high-contrast");
    } else {
      root.classList.remove("high-contrast");
    }
    
    // Highlight links
    if (s.highlightLinks) {
      root.classList.add("highlight-links");
    } else {
      root.classList.remove("highlight-links");
    }
    
    // Big cursor
    if (s.bigCursor) {
      root.classList.add("big-cursor");
    } else {
      root.classList.remove("big-cursor");
    }
    
    // Disable animations
    if (s.disableAnimations) {
      root.classList.add("disable-animations");
    } else {
      root.classList.remove("disable-animations");
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const increaseFontSize = () => {
    setSettings(prev => ({ ...prev, fontSize: Math.min(prev.fontSize + 10, 150) }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({ ...prev, fontSize: Math.max(prev.fontSize - 10, 80) }));
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="fixed bottom-4 left-4 z-50 h-14 w-14 rounded-full shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-primary-foreground/20"
            aria-label="פתח תפריט נגישות"
          >
            <Accessibility className="h-7 w-7" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-4" 
          side="top" 
          align="start"
          dir="rtl"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Accessibility className="h-5 w-5" />
                הגדרות נגישות
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetSettings}
                className="text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4 ml-1" />
                איפוס
              </Button>
            </div>
            
            <Separator />
            
            {/* Font Size Controls */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">גודל טקסט: {settings.fontSize}%</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={decreaseFontSize}
                  disabled={settings.fontSize <= 80}
                  aria-label="הקטן טקסט"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Slider
                  value={[settings.fontSize]}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, fontSize: value[0] }))}
                  min={80}
                  max={150}
                  step={10}
                  className="flex-1"
                  aria-label="גודל טקסט"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={increaseFontSize}
                  disabled={settings.fontSize >= 150}
                  aria-label="הגדל טקסט"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <Separator />
            
            {/* Toggle Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="high-contrast" className="flex items-center gap-2 cursor-pointer">
                  <Contrast className="h-4 w-4" />
                  ניגודיות גבוהה
                </Label>
                <Switch
                  id="high-contrast"
                  checked={settings.highContrast}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, highContrast: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="highlight-links" className="flex items-center gap-2 cursor-pointer">
                  <Link2 className="h-4 w-4" />
                  הדגשת קישורים
                </Label>
                <Switch
                  id="highlight-links"
                  checked={settings.highlightLinks}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, highlightLinks: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="big-cursor" className="flex items-center gap-2 cursor-pointer">
                  <MousePointer2 className="h-4 w-4" />
                  סמן גדול
                </Label>
                <Switch
                  id="big-cursor"
                  checked={settings.bigCursor}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, bigCursor: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="disable-animations" className="flex items-center gap-2 cursor-pointer">
                  <RotateCcw className="h-4 w-4" />
                  ביטול אנימציות
                </Label>
                <Switch
                  id="disable-animations"
                  checked={settings.disableAnimations}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, disableAnimations: checked }))}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* Accessibility Statement Link */}
            <Link 
              to="/accessibility" 
              className="flex items-center gap-2 text-sm text-primary hover:underline"
              onClick={() => setIsOpen(false)}
            >
              <FileText className="h-4 w-4" />
              הצהרת נגישות
            </Link>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default AccessibilityWidget;
